import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { apiError } from "../lib/api";
import { toast } from "sonner";
import { Lock, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { RatingScaleLegend, ItemRow, MIN_NOTE_CHARS } from "../components/Rating";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployeePortal() {
  const { token } = useParams();
  const [c, setC] = useState(null);
  const [error, setError] = useState(null);
  const [scores, setScores] = useState({});
  const [newGoal, setNewGoal] = useState("");
  const [addingGoal, setAddingGoal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = () => axios.get(`${API}/public/calibrations/${token}`)
    .then((r) => setC(r.data))
    .catch((e) => setError(apiError(e, "Link invalid or expired")));

  useEffect(() => { load(); }, [token]);

  if (error) {
    return (
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="cal-card p-10 text-center">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2">[ ERROR ]</div>
          <h2 className="font-display font-bold text-3xl mb-2">Link not found</h2>
          <p className="text-[#666]">{error}</p>
        </div>
      </div>
    );
  }
  if (!c) return <div className="p-10 font-mono-cal text-xs">[LOADING…]</div>;

  // If already submitted, show waiting or go to comparison
  if (c.employee_submitted) {
    return <SubmittedScreen c={c} token={token} />;
  }

  const items = [
    ...c.tasks.map((t) => ({ ...t, kind: "TASK", canDelete: false })),
    ...(c.goals || []).map((g) => ({ ...g, kind: "MY GOAL", canDelete: true })),
  ];

  const addGoal = async () => {
    if (!newGoal.trim()) return;
    setAddingGoal(true);
    try {
      await axios.post(`${API}/public/calibrations/${token}/goals`, { description: newGoal.trim() });
      setNewGoal("");
      toast.success("Goal added");
      await load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
    finally { setAddingGoal(false); }
  };

  const delGoal = async (gid) => {
    try {
      await axios.delete(`${API}/public/calibrations/${token}/goals/${gid}`);
      setScores((s) => { const n = { ...s }; delete n[gid]; return n; });
      toast.success("Goal removed");
      await load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  const setScore = (itemId, score) => setScores((s) => ({ ...s, [itemId]: { ...(s[itemId] || {}), score } }));
  const setNotes = (itemId, notes) => setScores((s) => ({ ...s, [itemId]: { ...(s[itemId] || { score: 3 }), notes } }));

  const allScored = items.every((it) => scores[it.id]?.score);
  const allNoted = items.every((it) => ((scores[it.id]?.notes || "").trim().length >= MIN_NOTE_CHARS));
  const submit = async () => {
    if (!allScored) { toast.error("Rate every item before submitting"); return; }
    if (!allNoted) { toast.error(`Every rating needs at least ${MIN_NOTE_CHARS} characters of evidence`); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/calibrations/${token}/submit`, {
        scores: items.map((it) => ({ item_id: it.id, score: scores[it.id].score, notes: scores[it.id].notes || "" }))
      });
      toast.success("Submitted. Thanks!");
      await load();
    } catch (err) { toast.error(apiError(err, "Submit failed")); setSubmitting(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <PortalHeader />
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] mb-3">[SECURE] Self-assessment · {c.period_label}</div>
      <h1 className="font-display font-bold text-5xl mb-2">{c.title}</h1>
      <p className="text-[#666] mb-6">
        Hi <span className="font-medium text-[#111]">{c.employee_name}</span> — rate how you believe you fulfilled each task across the {c.period_label.toLowerCase()}. Add any personal goals below before submitting.
      </p>

      <div className="cal-card p-4 mb-6 flex items-center gap-3" style={{ background: "#FF4F00", color: "#fff", borderColor: "#FF4F00" }} data-testid="privacy-banner">
        <Lock size={18} style={{ color: "#fff" }} />
        <div className="font-mono-cal text-xs uppercase tracking-widest" style={{ color: "#fff" }}>BLIND MODE · {c.manager_name}'s ratings are sealed until you submit</div>
      </div>

      <RatingScaleLegend />

      {/* Tasks */}
      <h2 className="font-display font-bold text-2xl mb-4 mt-8">Tasks from your PD</h2>
      <div className="space-y-5">
        {c.tasks.map((t, i) => (
          <ItemRow key={t.id} idx={i} kind="TASK" description={t.description}
            score={scores[t.id]?.score} notes={scores[t.id]?.notes}
            onScore={(n) => setScore(t.id, n)} onNotes={(n) => setNotes(t.id, n)}
            testPrefix="emp-task" />
        ))}
      </div>

      {/* Goals */}
      <div className="flex items-center justify-between mt-12 mb-4">
        <h2 className="font-display font-bold text-2xl">Your goals for this period</h2>
        <span className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">{c.goals?.length || 0} GOAL{(c.goals?.length || 0) === 1 ? "" : "S"}</span>
      </div>

      <div className="cal-card p-4 mb-4 flex gap-2">
        <input value={newGoal} onChange={(e) => setNewGoal(e.target.value)} placeholder="e.g. Complete advanced SQL certification"
          className="cal-input flex-1" maxLength={280} data-testid="goal-input"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addGoal(); } }} />
        <button onClick={addGoal} disabled={addingGoal || !newGoal.trim()} className="cal-btn" data-testid="add-goal-btn">
          <Plus size={14} /> Add goal
        </button>
      </div>

      <div className="space-y-5">
        {(c.goals || []).map((g, i) => (
          <div key={g.id} className="relative">
            <ItemRow idx={c.tasks.length + i} kind="MY GOAL" description={g.description}
              score={scores[g.id]?.score} notes={scores[g.id]?.notes}
              onScore={(n) => setScore(g.id, n)} onNotes={(n) => setNotes(g.id, n)}
              testPrefix="emp-goal" />
            <button onClick={() => delGoal(g.id)} className="absolute top-4 right-4 p-2 border border-[#111] bg-white hover:bg-[#111] hover:text-white transition"
              data-testid={`remove-goal-${i}`} title="Remove goal">
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-10 flex items-center gap-4">
        <div className="flex-1 font-mono-cal text-xs text-[#666]">
          {Object.keys(scores).filter((k) => scores[k]?.score).length} / {items.length} rated
        </div>
        <button onClick={submit} disabled={!allScored || submitting} className="cal-btn" data-testid="submit-self">
          {submitting ? "Submitting…" : "Submit & seal →"}
        </button>
      </div>
    </div>
  );
}

function SubmittedScreen({ c, token }) {
  const both = c.employee_submitted && c.manager_submitted;
  return (
    <div className="max-w-2xl mx-auto px-6 py-16">
      <PortalHeader />
      {both ? (
        <div className="cal-card p-10" data-testid="both-done">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#00A36C] mb-2">[ UNLOCKED ]</div>
          <h2 className="font-display font-bold text-3xl mb-2">Comparison ready</h2>
          <p className="text-[#666] mb-6">Both sides have submitted. Open the overlay to see where you align and where you diverge.</p>
          <Link to={`/e/${token}/done`} className="cal-btn" data-testid="open-comparison">View comparison →</Link>
        </div>
      ) : (
        <div className="cal-card p-10 text-center relative overflow-hidden" data-testid="employee-submitted">
          <div className="locked-pattern" />
          <CheckCircle2 className="mx-auto mb-4 text-[#00A36C]" size={40} />
          <h2 className="font-display font-bold text-3xl mb-2">Submitted</h2>
          <p className="text-[#666] mb-4">Your self-assessment is sealed. Waiting on {c.manager_name} to submit their rating.</p>
          <p className="font-mono-cal text-xs text-[#666]">You can safely close this window — come back to this link anytime.</p>
        </div>
      )}
    </div>
  );
}

function PortalHeader() {
  return (
    <div className="flex items-center gap-3 mb-10">
      <div className="h-7 w-7 border border-[#111] flex items-center justify-center">
        <div className="h-3 w-3 bg-[#FF4F00]" />
      </div>
      <span className="font-display font-bold text-xl tracking-tight">CALIBRATE</span>
      <span className="ml-auto font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">[SECURE LINK]</span>
    </div>
  );
}
